import { cn } from "@newxsystems/shared";
import { StratosAction } from "../StratosAction/StratosAction";
import { StratosMedia } from "../StratosMedia/StratosMedia";
import { StratosRichText } from "../StratosRichText/StratosRichText";
import type { ICallToActionSplitProps } from "./CallToActionSplit.props";

const layouts = {
  small: { image: "md:col-span-4 md:col-start-9", text: "md:col-span-8" },
  medium: { image: "md:col-span-5 md:col-start-8", text: "md:col-span-7" },
  large: { image: "md:col-span-6 md:col-start-7", text: "md:col-span-6" },
} as const;

export const CallToActionSplit = ({
  buttons,
  description,
  eyebrow,
  heading,
  image,
  imageWidth,
}: ICallToActionSplitProps) => {
  const layout = layouts[imageWidth || "medium"];

  return (
    <div
      className={cn(
        "grid grid-cols-1 items-center gap-7 md:gap-12",
        image ? "md:grid-cols-12" : buttons?.length && "md:grid-cols-2",
      )}
    >
      {eyebrow ? (
        <p
          className={cn(
            "order-1 border-b border-cloud-white/40 pb-2 font-stratos-body text-xs md:col-start-1",
            image && layout.text,
          )}
        >
          {eyebrow}
        </p>
      ) : null}
      {image ? (
        <StratosMedia
          aspectRatio={image.aspectRatio || "16:9"}
          className={cn(
            "order-2 w-full justify-self-end md:row-span-4 md:row-start-1",
            layout.image,
          )}
          media={image.image}
          sizes="(min-width: 768px) 50vw, 100vw"
        />
      ) : null}
      {heading ? (
        <h2
          className={cn(
            "order-3 font-stratos-display text-4xl font-bold leading-[0.98] tracking-[-0.025em] [overflow-wrap:anywhere] md:col-start-1 md:text-6xl",
            image && layout.text,
          )}
        >
          {heading}
        </h2>
      ) : null}
      {description ? (
        <StratosRichText
          className={cn(
            "order-4 max-w-[65ch] text-cloud-white md:col-start-1",
            image && layout.text,
          )}
          content={description}
        />
      ) : null}
      {buttons?.length ? (
        <div
          className={cn(
            "order-5 flex flex-wrap gap-3",
            image
              ? cn("md:col-start-1", layout.text)
              : "md:col-start-2 md:row-span-3 md:row-start-1 md:justify-self-end",
          )}
        >
          {buttons.map((button, index) => (
            <StratosAction
              action={button}
              className="border-cloud-white text-cloud-white"
              key={`${button.href}-${index}`}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};

CallToActionSplit.displayName = "Content::CallToActionSplit";
